/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import MuiToggle from '../../../../../Components/Inputs/MuiToggle/MuiToggle';
import { useMedicalHistoryOptional } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import { sanitizeMedicalHistoryBooleans } from '../utils/medicalHistoryDefaults';
import styles from './styles.module.scss';

const ClinicalDataStepEight = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { state, pathname } = useLocation();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const bypassRequired = useMedicalHistoryOptional(values);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data') || false;
  }, [state]);

  const [loading, setLoading] = useState(true);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [heightUnit, setHeightUnit] = useState<string>('ft/in');
  const [weightUnit, setWeightUnit] = useState<string>('lbs');
  const [heightCM, setHeightCM] = useState<string>('');
  const [heightFeet, setHeightFeet] = useState<string>('');
  const [heightInches, setHeightInches] = useState<string>('');
  const [weightKG, setWeightKG] = useState<string>('');
  const [weightLbs, setWeightLbs] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Tracks whether the patient actually edited this step, so we only confirm a
  // real save instead of showing a misleading toast on plain navigation.
  const userEditedRef = useRef(false);

  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>({
    id: 0,
    under_care_of_physician: null,
    conditions_treated: null,
    taking_medication: null,
    medications: null,
    allergic_to_medications: null,
    medications_allergies: null,
    using_substances: [],
    interested_in_stopping: null,
    height: '',
    weight: '',
    pharmacy_name: null,
    pharmacy_street_address: null,
    pharmacy_city: null,
    pharmacy_state: null,
    pharmacy_zip_code: null,
    pharmacy_phone_number: null,
  } as MedicalHistory);

  const isHeightValid =
    heightUnit === 'cm'
      ? heightCM && parseFloat(heightCM) > 0
      : heightFeet &&
        heightInches &&
        parseFloat(heightFeet) >= 0 &&
        parseFloat(heightInches) >= 0 &&
        (parseFloat(heightFeet) > 0 || parseFloat(heightInches) > 0);

  const isWeightValid =
    weightUnit === 'kg' ? weightKG && parseFloat(weightKG) > 0 : weightLbs && parseFloat(weightLbs) > 0;

  const isFormValid = isHeightValid && isWeightValid;

  const saveFormData = useCallback((): void => {
    const hasMeasurement = Boolean(isHeightValid) || Boolean(isWeightValid);
    if (hasUnsavedChanges && !loading && hasMeasurement) {
      const currentValues = {
        height: isHeightValid ? `${heightFeet}ft, ${heightInches}in` : '',
        weight: isWeightValid ? `${parseInt(weightLbs, 10)}lb` : '',
        height_cm: isHeightValid && heightCM ? parseInt(heightCM, 10) : null,
        weight_kg: isWeightValid && weightKG ? parseFloat(weightKG) : null,
        height_unit: heightUnit,
        weight_unit: weightUnit,
      };
      sessionStorage.setItem('heightAndWeight', JSON.stringify(currentValues));
    }
  }, [
    hasUnsavedChanges,
    heightCM,
    heightFeet,
    heightInches,
    heightUnit,
    isHeightValid,
    isWeightValid,
    loading,
    weightKG,
    weightLbs,
    weightUnit,
  ]);

  const goToNextStep = (): void => {
    roleNavigate('/clinical-data/end-step', {
      state: {
        caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
        isPatientForm: true,
        shouldSeeLastModal: state || '',
        patientId: selectedPatientId ? selectedPatientId : '',
      },
    });
  };

  const onSubmit = async (): Promise<void> => {
    const hasMeasurement = Boolean(isHeightValid) || Boolean(isWeightValid);
    if (bypassRequired && !hasMeasurement) {
      sessionStorage.removeItem('heightAndWeight');
      goToNextStep();
      return;
    }

    try {
      setLoading(true);
      setHasUnsavedChanges(false);

      const medicalHistoryPayload: MedicalHistorySubmit = {
        user_id: selectedPatientId as number,
        ...medicalHistory,
        height: isHeightValid ? `${heightFeet}ft, ${heightInches}in` : '',
        weight: isWeightValid ? `${parseInt(weightLbs, 10)}lb` : '',
        height_cm: isHeightValid && heightCM ? parseInt(heightCM, 10) : null,
        weight_kg: isWeightValid && weightKG ? parseFloat(weightKG) : null,
        height_unit: heightUnit,
        weight_unit: weightUnit,
      };

      const sanitizedPayload = sanitizeMedicalHistoryBooleans(medicalHistoryPayload, bypassRequired);
      if (!historyObjectId) {
        await addClinicalData('medical_history', sanitizedPayload);
      } else {
        await updateClinicalData('medical_history', historyObjectId.toString(), sanitizedPayload);
      }

      if (userEditedRef.current && !isIntakeProcess) {
        showSnackbar('Medical history saved successfully!', 'success');
      }
      sessionStorage.removeItem('heightAndWeight');
      setLoading(false);
      goToNextStep();
    } catch (error) {
      setHasUnsavedChanges(true);
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    let isActive = true;

    const hydrateClinicalData = async (): Promise<void> => {
      if (!selectedPatientId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const savedDraft = sessionStorage.getItem('heightAndWeight');
        const res = await listClinicalData(selectedPatientId);

        if (!isActive) return;

        if (res.medical_history) {
          setHistoryObjectId(res.medical_history.id || null);
          const parsedData = savedDraft ? JSON.parse(savedDraft) : res.medical_history;
          setHeightUnit(parsedData.height_unit || 'ft_in');
          if (parsedData?.height_cm) {
            const cm = Math.min(Number(parsedData.height_cm), 250);
            setHeightCM(cm.toString());

            const totalHeightInInches = cm / 2.54;
            let feet = Math.floor(totalHeightInInches / 12);
            let inches = Math.round(totalHeightInInches % 12);

            if (inches === 12) {
              inches = 0;
              feet += 1;
            }

            feet = Math.min(feet, 8);
            setHeightFeet(feet.toString());
            setHeightInches(inches.toString());
          } else if (parsedData?.height) {
            const heightParts = parsedData.height?.split('ft, ') || [];
            const feet = heightParts[0] ? parseInt(heightParts[0], 10) : 0;
            const inches = heightParts[1] ? parseInt(heightParts[1].replace('in', ''), 10) : 0;

            const validFeet = Math.min(feet, 8);
            const validInches = Math.min(inches, 11);

            setHeightFeet(validFeet.toString());
            setHeightInches(validInches.toString());

            const cm = ((validFeet * 12 + validInches) * 2.54).toFixed(0);
            setHeightCM(cm);
          }
          if (parsedData?.weight_kg) {
            const kg = Math.min(Number(parsedData.weight_kg), 250);
            setWeightKG(kg.toString());
            const lbs = kg / 0.453592;
            setWeightLbs(lbs.toFixed(0));
          } else if (parsedData?.weight) {
            const weightInLbs = parsedData.weight.replace('lb', '');
            const lbs = Math.min(Number(weightInLbs || 0), 550); // clamp for realism
            setWeightLbs(lbs.toString());
            setWeightKG((lbs * 0.453592).toFixed(2));
          }
          setWeightUnit(parsedData.weight_unit || 'lbs');
          setMedicalHistory(res.medical_history);
        }
      } catch (error) {
        if (isActive) {
          displayErrorDetails(error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
          setHasUnsavedChanges(true);
        }
      }
    };

    hydrateClinicalData();

    return () => {
      isActive = false;
    };
  }, [selectedPatientId]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={48} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>What is your current height and weight?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
              <div className={styles.clinicalDataIntroSubTitle}>
                We collect this information to ensure you are getting effective and safe prescriptions.
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                <div className={styles.measurementsForm}>
                  <div className={styles.measurementsFormTitle}>How tall are you?</div>
                  <div className={styles.measurementsFormInput}>
                    {heightUnit === 'cm' ? (
                      <input
                        type="number"
                        min={0}
                        max={250}
                        value={heightCM || ''}
                        onChange={(e) => {
                          userEditedRef.current = true;
                          let { value } = e.target;

                          if (value.length > 1 && value.startsWith('0')) {
                            value = String(parseInt(value, 10));
                          }

                          const cm = Math.min(Number(value || 0), 250);
                          setHeightCM(cm.toString());

                          const totalHeightInInches = cm / 2.54;
                          const feet = Math.floor(totalHeightInInches / 12);
                          let inches = Math.round(totalHeightInInches % 12);

                          if (inches === 12) {
                            inches = 0;
                            setHeightFeet((feet + 1).toString());
                          } else {
                            setHeightFeet(feet.toString());
                          }

                          setHeightInches(inches.toString());
                        }}
                      />
                    ) : (
                      <>
                        <input
                          type="number"
                          min={0}
                          max={8}
                          value={heightFeet || ''}
                          onChange={(e) => {
                            userEditedRef.current = true;
                            let { value } = e.target;
                            if (value.length > 1 && value.startsWith('0')) {
                              value = String(parseInt(value, 10));
                            }

                            const ft = Math.min(Number(value || 0), 8);
                            setHeightFeet(ft.toString());

                            const totalHeightInInches = ft * 12 + Number(heightInches || 0);
                            const cm = (totalHeightInInches * 2.54).toFixed(0);
                            setHeightCM(cm);
                          }}
                        />
                        <input
                          type="number"
                          min={0}
                          max={11}
                          value={heightInches || ''}
                          onChange={(e) => {
                            userEditedRef.current = true;
                            let { value } = e.target;
                            if (value.length > 1 && value.startsWith('0')) {
                              value = String(parseInt(value, 10));
                            }

                            const inches = Math.min(Number(value || 0), 11);
                            setHeightInches(inches.toString());

                            const totalHeightInInches = Number(heightFeet || 0) * 12 + inches;
                            const cm = (totalHeightInInches * 2.54).toFixed(0);
                            setHeightCM(cm);
                          }}
                        />
                      </>
                    )}
                    <MuiToggle
                      label="Use feet/inches or cm?"
                      hideLabel
                      options={[
                        { value: 'yes', label: 'cm' },
                        { value: 'no', label: 'ft/in' },
                      ]}
                      selectedValue={heightUnit === 'cm' ? 'yes' : 'no'}
                      onChange={(u) => {
                        userEditedRef.current = true;
                        if (u === 'yes') {
                          setHeightUnit('cm');
                        } else {
                          setHeightUnit('ft_in');
                        }
                      }}
                      height="40px"
                      width="150px"
                    />
                  </div>
                </div>
                <div className={styles.measurementsForm}>
                  <div className={styles.measurementsFormTitle}>How much do you weigh?</div>
                  <div className={styles.measurementsFormInput}>
                    {weightUnit === 'kg' ? (
                      <input
                        type="number"
                        min={0}
                        max={500}
                        value={weightKG || ''}
                        onChange={(e) => {
                          userEditedRef.current = true;
                          let { value } = e.target;
                          if (value.length > 1 && value.startsWith('0')) {
                            value = String(parseInt(value, 10));
                          }
                          setWeightKG(value);
                          const totalWeightInLbs = parseFloat(value) / 0.453592;
                          setWeightLbs(totalWeightInLbs.toFixed(2));
                        }}
                        style={{ width: '200px' }}
                      />
                    ) : (
                      <input
                        type="number"
                        min={0}
                        max={1000}
                        value={weightLbs || ''}
                        onChange={(e) => {
                          userEditedRef.current = true;
                          setWeightLbs(e.target.value);
                          const totalWeightInKg = parseFloat(e.target.value) * 0.453592;
                          setWeightKG(totalWeightInKg.toFixed(2));
                        }}
                        style={{ width: '200px' }}
                      />
                    )}
                    <MuiToggle
                      label="Use pounds or kg?"
                      hideLabel
                      options={[
                        { value: 'yes', label: 'kg' },
                        { value: 'no', label: 'lbs' },
                      ]}
                      selectedValue={weightUnit === 'kg' ? 'yes' : 'no'}
                      onChange={(u) => {
                        userEditedRef.current = true;
                        if (u === 'yes') {
                          setWeightUnit('kg');
                        } else {
                          setWeightUnit('lbs');
                        }
                      }}
                      height="40px"
                      width="150px"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={loading || (!isFormValid && !bypassRequired)}
                onClick={(e) => {
                  e.preventDefault();
                  if (isFormValid || bypassRequired) {
                    onSubmit();
                  } else {
                    showSnackbar('Please fill in all required fields before proceeding.', 'error');
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepEight;
